import {
  FacebookLogoIcon,
  InstagramLogoIcon,
  LinkedinLogoIcon,
  TwitterLogoIcon,
  SpinnerGapIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import Link from "next/link";

import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { PartbotPunkAlt2 } from "./partbot-punk";

const footerNavigation = {
  solutions: [
    { name: "Vehicle Search Plugins", href: "/" },
    { name: "Parts Cataloguing (PIM)", href: "/brands" },
    { name: "Stockist Fulfilment Management", href: "/distributors" },
  ],
  products: [
    {
      name: "Number Plate Search",
      href: "https://plate-search.partbot.io",
    },
    { name: "Vehicle Search Plugin", href: "https://mmyvs.partbot.io" },
    { name: "Live Shipping Rates", href: "/shipping-rates", prefetch: false },
    { name: "Pricing", href: "/pricing" },
  ],
  support: [
    { name: "Help Centre", href: "https://help.partbot.io/" },
    { name: "Contact Us", href: "/contact" },
    { name: "Status", href: "https://status.partbot.io/" },
    { name: "About", href: "/about" },
    // { name: "Blog", href: "/blog" },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "EULA", href: "/eula" },
    { name: "SLA", href: "/sla" },
  ],
  social: [
    {
      name: "LinkedIn",
      href: "https://linkedin.com.au/company/partbot",
      icon: (props) => (
        <div {...props}>
          <LinkedinLogoIcon className="h-5 w-5" />
        </div>
      ),
    },
    {
      name: "Twitter",
      href: "https://twitter.com/partbotapp",
      icon: (props) => (
        <div {...props}>
          <TwitterLogoIcon className="h-5 w-5" />
        </div>
      ),
    },
    {
      name: "Facebook",
      href: "https://facebook.com/PartbotApp",
      icon: (props) => (
        <div {...props}>
          <FacebookLogoIcon className="h-5 w-5" />
        </div>
      ),
    },
    {
      name: "Instagram",
      href: "https://instagram.com/PartbotApp",
      icon: (props) => (
        <div {...props}>
          <InstagramLogoIcon className="h-5 w-5" />
        </div>
      ),
    },
  ],
};

export default function Footer() {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const subscribeToNewsletter = async (email) => {

    setLoading(true);
    // post to the email API endpoint
    const response = await fetch("/api/email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        properties: {
          newsletter: true,
        },
      }),
    });

    if (response.ok) {
      setLoading(false);
      setSubscribed(true);
    }
  };

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1, transition: { duration: 1, delay: 0.5 } }}
      viewport={{ once: true }}
      aria-labelledby="footerHeading"
      className="container mx-auto"
    >
      <div className="mb-6 rounded-3xl border border-emerald-300/10 bg-stone-950 p-0 md:mb-16 md:p-3">
        <h2 id="footerHeading" className="sr-only">
          Footer
        </h2>
        <div className="px-6 pb-8 pt-8 lg:px-6 lg:pt-8">
          <div className="w-full xl:grid xl:grid-cols-3 xl:gap-8">
            <div className="col-span-2 gap-6 md:grid md:grid-cols-4">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-stone-100">
                  Solutions
                </h3>
                <ul className="mt-4 space-y-2">
                  {footerNavigation.solutions.map((item) => (
                    <li key={item.name}>
                      <Link
                        href={item.href}
                        prefetch={item.prefetch}
                        className="inline-block text-xs leading-4! text-stone-400 transition hover:text-stone-200"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-12 md:mt-0">
                <h3 className="text-sm font-bold uppercase tracking-widest text-stone-100">
                  Products
                </h3>
                <ul className="mt-4 space-y-2">
                  {footerNavigation.products.map((item) => (
                    <li key={item.name}>
                      <Link
                        href={item.href}
                        prefetch={item.prefetch}
                        className="inline-block text-xs leading-4! text-stone-400 transition hover:text-stone-200"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-12 md:mt-0">
                <h3 className="text-sm font-bold uppercase tracking-widest text-stone-100">
                  Support
                </h3>
                <ul className="mt-4 space-y-2">
                  {footerNavigation.support.map((item) => (
                    <li key={item.name}>
                      <Link
                        href={item.href}
                        prefetch={item.prefetch}
                        className="inline-block text-xs leading-4! text-stone-400 transition hover:text-stone-200"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-12 md:mt-0">
                <h3 className="text-sm font-bold uppercase tracking-widest text-stone-100">
                  Legal
                </h3>
                <ul className="mt-4 space-y-2">
                  {footerNavigation.legal.map((item) => (
                    <li key={item.name}>
                      <Link
                        href={item.href}
                        prefetch={item.prefetch}
                        className="inline-block text-xs leading-4! text-stone-400 transition hover:text-stone-200"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-12 xl:mt-0">
              <h3 className="text-sm font-bold uppercase tracking-widest text-emerald-500">
                Subscribe to our newsletter
              </h3>
              <p className="mt-4 text-balance text-sm text-emerald-400/40">
                Learn how to succeed with selling automotive parts online.
              </p>
              <form
                className="mt-4 flex max-w-md flex-col"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const email = formData.get("email");
                  subscribeToNewsletter(email);
                }}
              >
                <div className="relative flex w-full flex-col md:flex-row">
                  {subscribed ? (
                    <div className="w-full rounded-md border border-emerald-300/10 bg-emerald-600/20 p-3 text-center text-sm">
                      Thanks for subscribing!
                    </div>
                  ) : (
                    <>
                      <label htmlFor="email" className="sr-only">
                        Email address
                      </label>
                      <div className="flex w-full flex-col space-y-1.5">
                        <Input
                          type="email"
                          name="email"
                          id="email"
                          autoComplete="email"
                          placeholder="Enter your email"
                          required
                        />
                      </div>
                      <div className="ml-0 mt-3 rounded-md md:ml-2 md:mt-0">
                        <Button className="w-full" type="submit">
                          <div className="flex items-center justify-center gap-2">
                            <span>Subscribe</span>
                            {loading && (
                              <SpinnerGapIcon className="animate-spin" />
                            )}
                          </div>
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
          <div className="mt-6 w-full text-emerald-700 opacity-20 transition hover:opacity-100 md:opacity-5">
            <PartbotPunkAlt2 />
          </div>
          <div className="mt-4 border-t border-dashed border-stone-600/75 pt-8 md:flex md:items-center md:justify-between lg:mt-6">
            <div className="flex flex-col items-start space-y-5 md:order-2 md:flex-row md:items-center md:space-x-6 md:space-y-0">
              <div className="flex items-center space-x-2 text-xs text-stone-400/75">
                <span>🇦🇺</span>
                <span>Made in Australia</span>
              </div>
              <div className="flex space-x-6">
                {footerNavigation.social.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-stone-400/75 hover:text-stone-200"
                  >
                    <span className="sr-only">{item.name}</span>
                    <item.icon aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3 text-xs text-stone-400/75 md:order-1 md:mt-0 md:flex-nowrap md:items-center">
              <div>&copy; Partbot Software Pty Ltd.</div>
              <div>ABN 816 660 546 73</div>
            </div>
          </div>
        </div>
      </div>
    </motion.footer>
  );
}
